<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import DateTime from '@/components/DateTime.vue'
import DataTable from '@/components/dataTable/DataTable.vue'
import DataTableCreateButton from '@/components/dataTable/DataTableCreateButton.vue'
import { Issue, IssueLabel, PermissionOrganization, User } from '@/generated/graphql'
import { computed, ref } from 'vue'
import { uniqueArray } from '@/app'
import { ComponentExposed } from 'vue-component-type-helpers'
import IssueDialog from '@/components/issue/IssueDialog.vue'
import CreateIssueDialog, { CreateIssueSeed } from '@/components/issue/CreateIssueDialog.vue'
import useAuthorization from '@/composables/useAuthorization'

type LocalUser = Pick<User, 'id' | 'firstname' | 'lastname'>
type LocalLabel = Pick<IssueLabel, 'id' | 'name' | 'color'>
type LocalIssue = Pick<Issue, 'id' | 'name' | 'createdAt' | 'resolvedAt'> & {
  assignedToUsers: LocalUser[]
  labels: LocalLabel[]
}
type LocalLink = { issue: LocalIssue }

const props = defineProps<{
  links: LocalLink[]
  createSeed?: CreateIssueSeed
  withResolved?: boolean
}>()

const { t } = useI18n()
const { hasOrgAuthz } = useAuthorization()

const unresolvedIssues = computed(() => {
  const all = props.links
    .filter((l) => props.withResolved || !l.issue.resolvedAt)
    .map((l) => l.issue)
  return uniqueArray<LocalIssue, string>(all, (item) => item.id)
})
const issueDialog = ref<ComponentExposed<typeof IssueDialog>>()
const selectIssue = (_: unknown, row: { item: LocalIssue }) => {
  issueDialog.value?.open(row.item.id)
}
const createIssueDialog = ref<ComponentExposed<typeof CreateIssueDialog>>()
const createIssue = () => {
  createIssueDialog.value?.open(props.createSeed)
}
const issueHeaders = [
  {
    key: 'name',
    title: t('entity.issue.field.name'),
  },
  {
    key: 'assignedToUsers',
    title: t('entity.issue.field.assignedToUsers'),
    sortable: false,
  },
  {
    key: 'labels',
    title: t('entity.issueLabel.plural'),
    sortable: false,
  },
  {
    key: 'createdAt',
    title: t('entity.issue.field.createdAt'),
  },
  {
    key: 'actions',
    width: 100,
    sortable: false,
    align: 'end',
  },
]
const issueSort = [{ key: 'createdAt', order: 'desc' }]
</script>

<template>
  <data-table
    :headers="issueHeaders"
    :sort-by="issueSort"
    :items="unresolvedIssues"
    :items-per-page="-1"
    v-bind="$attrs"
    @click:row="selectIssue"
  >
    <template #header.actions>
      <data-table-create-button
        v-if="hasOrgAuthz(PermissionOrganization.OrgIssueWrite)"
        @click="createIssue"
      />
    </template>
    <template #item.assignedToUsers="{ item }: { item: LocalIssue }">
      <template v-if="item.assignedToUsers.length > 0">
        {{ item.assignedToUsers[0].firstname }} {{ item.assignedToUsers[0].lastname }}
      </template>
      <v-chip v-if="item.assignedToUsers.length > 1" density="compact" class="ml-2">
        +{{ item.assignedToUsers.length - 1 }}
      </v-chip>
    </template>
    <template #item.labels="{ item }: { item: LocalIssue }">
      <div class="label-chips">
        <v-chip v-for="l in item.labels" :key="l.id" density="compact">
          <span class="label-color-dot" :style="{ background: l.color }" />
          {{ l.name }}
        </v-chip>
      </div>
    </template>
    <template #item.createdAt="{ item }: { item: LocalIssue }">
      <date-time :model-value="item.createdAt" />
    </template>
  </data-table>

  <issue-dialog ref="issueDialog" />
  <create-issue-dialog ref="createIssueDialog" />
</template>

<style scoped lang="scss">
.label-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 4px 0;
}
.label-color-dot {
  width: 10px;
  height: 10px;
  display: inline-block;
  border-radius: 2px;
  margin-right: 6px;
  border: 1px solid #ccc;
}
</style>
